import React, { useState, useRef, useEffect } from 'react';
import { propTypes } from '../../../util/types';
import { useIntl } from '../../../util/reactIntl';
import { AspectRatioWrapper, ResponsiveImage, Modal, Button, Icons } from '../../../components';
import Slider from 'react-slick';
import { isArrayLength } from '../../../util/genericHelpers';
import IconRightArrow from '../../../components/IconRightArrow/IconRightArrow';
import IconBackArrow from '../../../components/IconBackArrow/IconBackArrow';
import '../../../styles/zoomerDefaults.css';
import communityPhotoIcon from '../../../assets/communityPhotoIcon.png';

import css from './ListingImageGallery.module.css';
import '../../../styles/slick/slick.css';
import '../../../styles/slick/slick-theme.css';

const isWindowDefined = typeof window !== 'undefined';
const isMobileLayout = isWindowDefined && window.innerWidth < 768;

/**
 * Transforms Sharetribe image data into a simplified image array
 * with `src` and `alt` fields only.
 *
 * @param {Array} imageObjects - Array of image objects from Sharetribe.
 * @returns {Array} - Array in the format [{ src: '', alt: '' }]
 */
const getImageVariantUrl = (image, variantNames = []) => {
  const variants = image?.attributes?.variants || {};

  for (const variantName of variantNames) {
    const variant = variants[variantName];
    if (variant?.url) return variant.url;
  }

  const fallbackVariant = Object.values(variants)[0];
  return (
    fallbackVariant?.url || image?.attributes?.communityPhotoUrl || image?.communityPhotoUrl || ''
  );
};

const isCommunityPhoto = image =>
  Boolean(image?.attributes?.communityPhoto || image?.communityPhoto);

function transformImageData(imageObjects) {
  if (!Array.isArray(imageObjects)) return [];

  return imageObjects.map((imgObj, index) => {
    const src = getImageVariantUrl(imgObj?.image, [
      'listing-card-6x',
      'scaled-xlarge',
      'scaled-large',
      'scaled-medium',
    ]);

    return {
      src,
      alt: imgObj?.alt || `Photo ${index + 1}/${imageObjects.length}`,
    };
  });
}

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const { images = [], imageVariants, onManageDisableScrolling = () => false } = props;

  const [showAll, onShowAll] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null); // { index }
  const [isZoomModalOpen, setZoomModalOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const intl = useIntl();
  const largeImageRefs = useRef([]);

  const communityLabel = intl.formatMessage({ id: 'ListingImageGallery.communityPhotoLabel' });

  const items = images.map((img, i) => {
    const communityPhoto = isCommunityPhoto(img);
    const alt = communityPhoto
      ? communityLabel
      : intl.formatMessage(
          { id: 'ListingImageGallery.imageAltText' },
          { index: i + 1, count: images.length }
        );
    const thumbAlt = communityPhoto
      ? communityLabel
      : intl.formatMessage(
          { id: 'ListingImageGallery.imageThumbnailAltText' },
          { index: i + 1, count: images.length }
        );

    return {
      key: `listing-gallery-${i}`,
      original: '',
      alt,
      thumbAlt,
      image: img,
      isCommunity: communityPhoto,
    };
  });

  const activeViewerItem = items[viewerIndex];

  const renderItem = item => {
    return (
      <AspectRatioWrapper key={item.key} width={1} height={1} className={css.itemWrapper}>
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
          />
          {item.isCommunity ? (
            <div className={css.communityIcon} aria-hidden="true">
              <img src={communityPhotoIcon} alt="" />
            </div>
          ) : null}
        </div>
      </AspectRatioWrapper>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  function RenderNextArrow(props) {
    const { className, onClick } = props;
    return (
      <div className={className} onClick={onClick}>
        <IconRightArrow className={css.icon} />
      </div>
    );
  }

  function RenderPrevArrow(props) {
    const { className, onClick } = props;
    return (
      <div className={className} onClick={onClick}>
        <IconBackArrow className={css.icon} />
      </div>
    );
  }

  const settings = {
    dots: isMobileLayout,
    arrows: !isMobileLayout,
    dotsClass: 'slick-dots slick-thumb',
    fade: true,
    infinite: true,
    lazyLoad: true,
    autoplay: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    pauseOnHover: true,
    nextArrow: <RenderNextArrow />,
    prevArrow: <RenderPrevArrow />,
  };

  const renderSliderData = selectedImage ? (
    <div className={css.gallaryDesktopSec} />
  ) : (
    <>
      {isMobileLayout ? (
        <Slider className={css.gallerySec} {...settings}>
          {items.map(renderItem)}
        </Slider>
      ) : (
        <div className={css.gallaryDesktopSec}> {items?.slice(0, 5).map(renderItem)}</div>
      )}
    </>
  );

  const renderShowAllModal = (
    <Modal
      usePortal
      id="ListingImageGallery"
      modalTitle={`View All (${items?.length})`}
      isOpen={showAll}
      onClose={() => onShowAll(false)}
      onManageDisableScrolling={onManageDisableScrolling}
      isGalleryModal={true}
    >
      <div className={css.modalGallery}>
        <h2>{intl.formatMessage({ id: 'ListingImageGallery.photoTour' })}</h2>
        <div className={css.smallImagesWrapper}>
          {isArrayLength(items) &&
            items.map((item, index) => {
              const photoLabel = intl.formatMessage({ id: 'ListingImageGallery.imageTitle' });
              const img = getImageVariantUrl(item.image, [
                'listing-card',
                'listing-card-2x',
                'scaled-small',
              ]);
              const label = item.isCommunity ? communityLabel : `${photoLabel} ${index + 1}`;
              return (
                <div
                  className={css.smallThumbnailImages}
                  key={item.key}
                  onClick={() =>
                    largeImageRefs.current[index]?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    })
                  }
                >
                  <div className={css.smallThumbnailGrid}>
                    <img src={img} alt={item.thumbAlt || item.alt || `Image ${index + 1}`} />
                    {item.isCommunity ? (
                      <div className={css.communityThumbnailIcon} aria-hidden="true">
                        <img src={communityPhotoIcon} alt="" />

                        {/* <Icons name="community" /> */}
                      </div>
                    ) : null}
                  </div>
                  <div
                    className={`${css.smallThumbnailInfos} ${
                      item.isCommunity ? css.communityLabel : ''
                    }`}
                  >
                    {label}
                  </div>
                </div>
              );
            })}
        </div>

        <div className={css.largeImages}>
          {isArrayLength(items) &&
            items.map((item, index) => {
              const baseImage = getImageVariantUrl(item.image, [
                'listing-card-6x',
                'scaled-xlarge',
                'scaled-large',
                'scaled-medium',
              ]);
              return (
                <div
                  className={css.categoryImages}
                  key={item.key}
                  ref={el => (largeImageRefs.current[index] = el)}
                >
                  <div className={css.categoryImagesTitle}>
                    {item.isCommunity
                      ? communityLabel
                      : `${intl.formatMessage({ id: 'ListingImageGallery.imageTitle' })} ${index +
                          1}`}
                  </div>
                  <div
                    onClick={() => {
                      setSelectedImage({ index });
                      setViewerIndex(index);
                      setZoomModalOpen(true);
                    }}
                  >
                    <div className={css.smallThumbnailGrid}>
                      <img src={baseImage} alt={item.alt || `Image ${index + 1}`} />
                      {item.isCommunity ? (
                        <div className={css.communitybadge} aria-hidden="true">
                          <img src={communityPhotoIcon} alt="" />
                          {/* <Icons name="community" />  */}
                          {communityLabel}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </Modal>
  );

  const [Viewer, setViewer] = useState(null);

  useEffect(() => {
    // Only load Viewer when window is defined
    if (typeof window !== 'undefined') {
      import('react-viewer').then(module => {
        setViewer(() => module.default);
      });
    }
  }, []);

  return (
    <div className={css.gallarySlider}>
      {renderSliderData}
      <div className={css.viewPhotos}>
        <Button onClick={() => onShowAll(!showAll)}>
          {intl.formatMessage({ id: 'ListingImageGallery.showAll' })}({items?.length})
        </Button>

        {isZoomModalOpen && selectedImage && Viewer ? (
          <>
            <Viewer
              visible={isZoomModalOpen}
              noNavbar
              zIndex={9999}
              activeIndex={viewerIndex}
              onClose={() => {
                setSelectedImage(null);
                setZoomModalOpen(false);
                setViewerIndex(0);
              }}
              images={transformImageData(items)}
              onChange={(activeImage, index) => setViewerIndex(index)}
              rotatable={false}
              scalable={false}
              className={css.toolBarContainer}
              zoomSpeed={0.4} // 3-4x zoom increment boost
              drag={true} // ensure panning works after zoom
              zoomable
              downloadable={false}
            />
            {activeViewerItem?.isCommunity ? (
              <div className={css.communityBadge}>
                <img src={communityPhotoIcon} alt="" aria-hidden="true" />
                <span>{communityLabel}</span>
              </div>
            ) : null}
          </>
        ) : (
          renderShowAllModal
        )}
      </div>
    </div>
  );
};

export default ListingImageGallery;
